import React, { useCallback, useEffect, useRef, memo } from 'react';
import * as echarts from 'echarts';
import { isEqual, debounce } from 'lodash-es';
import { defaultTooltipFormatter } from './utils/common';
import '../../index.scss';
import './index.scss';

const resizeRenderMapEchart = (chartEle: any) => {
  const myChart: any = echarts.getInstanceByDom(chartEle);
  // let option = myChart.getOption();
  // myChart.setOption(option);
  myChart.resize();
}

type EventTypeEchartPropsTypes = {
  title: string; // 浮窗卡片的标题
  data: any,
  yAxisLMaxText: number | string | any, // 左侧y轴刻度占比位置最宽的文案
  isDoubleYAxis?: boolean;
  yAxisRMaxText?: number | string | any, // 右侧y轴刻度占比位置最宽的文案
  style?: React.CSSProperties,
  refresh?: any, // 这个值一定是变化的
  fontSize?: number,
  tooltipPosition?: 'top' | 'bottom', // 浮窗在hover位置上面还是下面
};

const DEFAULT_PROPS: EventTypeEchartPropsTypes = {
  title: '',
  data: null,
  yAxisLMaxText: 0,
  isDoubleYAxis: false,
  yAxisRMaxText: 0,
  style: {},
  refresh: null,
  fontSize: 12,
  tooltipPosition: 'bottom',
};

const EventTypeEchart = (props: EventTypeEchartPropsTypes) => {

  const {
    title,
    data,
    yAxisLMaxText,
    isDoubleYAxis,
    yAxisRMaxText,
    style,
    refresh,
    fontSize,
    tooltipPosition,
  } = props;

  const chartRef = useRef(null);

  //监听屏幕变化，重绘图表
  const listenerResize = (chart: any) => {
    if (chart) debounce(resizeRenderMapEchart(chart), 300);
  }

  //初始化图表，设置配置项
  const renderChart = useCallback(() => {
    if (chartRef.current) {
      let myChart = echarts.getInstanceByDom(chartRef.current);
      if (myChart == null) {
        myChart = echarts.init(chartRef.current);
      }

      const seriseData = data?.map((item: any, index: number) => {
        let width = 12;
        let eventsCount = 0; // 事件数量
        const events = item?.events;
        if (events?.length) {
          for (let i = 0; i < events?.length; i ++) {
            const t = events?.[i];
            eventsCount = eventsCount + t?.events?.length || 0;
          }
        }
        let text: string | number = eventsCount;
        if (eventsCount > 99) {
          width = 24;
          text = "99+";
        } else if (eventsCount > 9) {
          width = 20;
        }

        return {
          name: index,
          value: 0,
          label: {
            show: eventsCount > 0,
            width,
            formatter: () => text,
          },
          ...item,
        }
      });

      let options: any = {
        textStyle: {fontSize},
        title: {
          show: false,
        },
        legend: {
          show: false,
        },
        grid: {
          x: '0%',
          y: "0%",
          x2: '0%',
          y2: "-66%",
          containLabel: true,
        },
        tooltip: {
          // show: true,
          // showContent: true,
          // alwaysShowContent: true,
          borderWidth: 0,
          backgroundColor: 'rgba(255, 255, 255, 0)',
          className: `echarts-tooltip-custom-style ${fontSize === 14 ? 'font14' : ''}`,
          trigger: 'item',
          showDelay: 0,
          transitionDuration: 0.2,
          enterable: true,
          axisPointer: {
            type: 'shadow',
            shadowStyle: {
              color: 'rgba(187, 187, 187, 0.2)',
            },
          },
          position: function (point: [number, number], params: any, dom: any, rect: any, size: any) { // 浮窗位置
            const { dataIndex } = params, { contentSize } = size;
            const count = seriseData?.length || 0;
            const flag = (dataIndex + 1) <= (count / 2);
            const verticalOffset = tooltipPosition === 'top' ? -(contentSize?.[1] + 20) : 20;
            // 一半之前浮窗显示在右边，一半之后浮窗显示在左边
            const position = flag ? [point?.[0], point?.[1] + verticalOffset] : [point?.[0] - contentSize?.[0], point?.[1] + verticalOffset];
            return position;
          },
          formatter: (params: any) => defaultTooltipFormatter(title, params),
        },
        yAxis: {
          type: 'value',
          name: 'left',
          splitLine: {
            show: true,
            lineStyle: {
              type: 'dashed',
              color: 'rgba(0, 0, 0, 0)',
            },
          },
          axisLabel: {
            show: true,
            color: 'rgba(0, 0, 0, 0)',
            formatter: (v: any) => yAxisLMaxText,
          },
        },
        xAxis: [
          {
            type: 'category',
            show: false,
            data: data?.map((t: any, index: number) => index) || [],
            interval: 1,
            axisLabel: {
              color: '#888888',
              fontSize: 10,
              align: 'center',
            },
            axisTick: {
              alignWithLabel: true,
              interval: 1,
            },
          },
        ],
        series: [
          {
            id: "eventType",
            type: "line",
            showSymbol: true,
            showAllSymbol: false,
            data: seriseData,
            lineStyle: {
              color: "rgba(255, 255, 255, 0)",
            },
            symbolSize: 0,
            itemStyle: {
              color: "rgba(0, 0, 0, 0)",
            }, // 圆形样式
            label: { // label标签样式
              show: true,
              backgroundColor: "#fff",
              borderWidth: 0.5,
              borderColor: "rgba(0, 0, 0, 0.3)",
              fontSize: 10,
              fontWeight: 500,
              color: "#888888",
              position: "inside",
              align: 'center',
              verticalAlign: "bottom",
              width: 12,
              height: 12,
              borderRadius: 7,
            },
            emphasis: { // 高亮的图形和标签样式
              disabled: false,
              scale: false, // 关闭高亮放大效果
              label: {
                color: "#fff",
                backgroundColor: "#326ff0",
                borderColor: "#326ff0",
              }
            },
          },
        ],
      };
      // 双y轴情况
      if (isDoubleYAxis) {
        options.yAxis = [
          options.yAxis,
          {
            ...options.yAxis,
            name: 'right',
            axisLabel: {
              show: true,
              color: 'rgba(0, 0, 0, 0)',
              formatter: (v: any) => yAxisRMaxText,
            },
          }
        ];
        options.series = [
          {
            ...options.series[0],
            yAxisIndex: 0,
          },
          {
            ...options.series[0],
            id: 'eventTypes',
            yAxisIndex: 1,
          }
        ];
      }
      options && myChart.setOption(options, true);
      myChart.resize();
    }

    //监听屏幕变化事件 传入当前chart实例
    window.addEventListener('resize', () => listenerResize(chartRef.current));
    return () => {
      window.removeEventListener('resize', listenerResize as EventListenerOrEventListenerObject);
    };
  }, [data, yAxisLMaxText, yAxisRMaxText, tooltipPosition]);

  // 初始化
  useEffect(() => {
    renderChart();
    return () => {
      if (chartRef.current) {
        (echarts.getInstanceByDom(chartRef.current) as echarts.ECharts).dispose();
      }
    };
  }, [data, yAxisLMaxText, yAxisRMaxText]);

  // 手动执行更新图表数据
  useEffect(() => {
    if (!refresh) return;
    if (chartRef.current) resizeRenderMapEchart(chartRef.current);
  }, [refresh]);

  return (
    <div
      ref={chartRef}
      style={Object?.assign(
        {
          width: '100%',
          height: 20,
          position: 'absolute',
          left: 0,
          bottom: 20,
        },
        style || {},
        )}
    />
  );
};

EventTypeEchart.defaultProps = DEFAULT_PROPS;

export default memo(
  EventTypeEchart,
  (prevProps: any, nextProps: any) => {
    return isEqual(prevProps, nextProps);
  },
);
